import {
  Disclaimer,
  Div,
  DropdownMenuComponent,
  P,
  Radio,
  useOnClickOutside,
  lightTheme,
} from '@vgrubs/components';
import { ReactNode, useEffect, useId, useRef, useState } from 'react';

import {
  AdminSelectWrapperChildStyled,
  AdminSelectWrapperStyled,
  ChevronStyled,
  SelectedStyled,
  SelectOption,
} from './AdminCustomSelect.styled';

export type SelectOption<T = string> = {
  name: string;
  value: T;
  icon?: ReactNode;
  /** Rendered instead of `name` when the option needs markup (a badge, say). */
  label?: ReactNode;
};

export const AdminCustomSelect = <T = string,>({
  onChange,
  value,
  label,
  options = [],
  emptyPlaceholder = 'No data',
  $textCenter = false,
  placeholder = 'Select option',
  $shadowDesign = true,
  disabledOptions = [],
  multiSelect = false,
}: {
  onChange: (value: T | null | T[]) => void;
  value: T | null | T[];
  label?: string;
  options?: SelectOption<T>[];
  emptyPlaceholder?: string;
  $textCenter?: boolean;
  placeholder?: string;
  $shadowDesign?: boolean;
  disabledOptions?: T[];
  multiSelect?: boolean;
}) => {
  const [dropdown, setDropdown] = useState(false);

  // Ensure disabledOptions is never null to prevent runtime errors
  const safeDisabledOptions = disabledOptions || [];

  // Helper to get current values as array
  const getCurrentValues = (): T[] => {
    if (multiSelect) {
      if (Array.isArray(value)) {
        return value;
      }
      return value !== null ? [value] : [];
    }
    return value !== null && !Array.isArray(value) ? [value] : [];
  };

  // Helper to check if item is selected
  const isSelected = (itemValue: T): boolean => {
    const currentValues = getCurrentValues();
    return currentValues.some((v) => v === itemValue);
  };

  // Helper to get option by value
  const getOptionByValue = (itemValue: T): SelectOption<T> | undefined => {
    return options.find((opt) => opt.value === itemValue);
  };

  const dropdownRef = useRef(null);
  useOnClickOutside(dropdownRef, () => dropdown && setDropdown(false));

  // a11y: stable ids for combobox ↔ label ↔ listbox linkage
  const labelId = useId();
  const listboxId = useId();

  // a11y: ESC closes the dropdown (matches AdminOptionsDropdown /
  // AdminRolePicker / modal pattern in this codebase).
  useEffect(() => {
    if (!dropdown) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setDropdown(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dropdown]);

  const handleSelect = (selectedValue: T | null) => {
    if (multiSelect) {
      const currentValues = getCurrentValues();
      let newValues: T[];

      if (selectedValue === null) {
        newValues = [];
      } else if (currentValues.includes(selectedValue)) {
        // Remove if already selected
        newValues = currentValues.filter((v) => v !== selectedValue);
      } else {
        // Add if not selected
        newValues = [...currentValues, selectedValue];
      }

      onChange(newValues);
    } else {
      onChange(selectedValue);
      setDropdown(false);
    }
  };

  useEffect(() => {
    if (!multiSelect) {
      setDropdown(false);
    }
  }, [value, multiSelect]);

  // Render selected items
  const RenderSelectedItems = () => {
    const currentValues = getCurrentValues();

    if (currentValues.length === 0) {
      return placeholder;
    }

    if (multiSelect) {
      return (
        <>
          <Div $desktop $flex $wrap $gap="12px" $gapSM="8px" $alignCenter>
            {currentValues.slice(0, 2).map((itemValue, index) => {
              const option = getOptionByValue(itemValue);
              return (
                <Div key={index} $flex $alignCenter $gap="8px" $gapSM="4px">
                  {option?.name || String(itemValue)}
                  {index < Math.min(2, currentValues.length) - 1 && ','}
                </Div>
              );
            })}
            {currentValues.length > 2 && (
              <Disclaimer $m="0" $color={lightTheme.darkGray}>
                +{currentValues.length - 2} more
              </Disclaimer>
            )}
          </Div>
          <Div $mobile $flex $wrap $gap="12px" $gapSM="8px" $alignCenter>
            {currentValues.slice(0, 1).map((itemValue, index) => {
              const option = getOptionByValue(itemValue);
              return (
                <Div key={index} $flex $alignCenter $gap="8px" $gapSM="4px">
                  {option?.name || String(itemValue)}
                  {index < Math.min(1, currentValues.length) - 1 && ','}
                </Div>
              );
            })}
            {currentValues.length > 1 && (
              <Disclaimer $m="0" $color={lightTheme.darkGray}>
                +{currentValues.length - 1} more
              </Disclaimer>
            )}
          </Div>
        </>
      );
    } else {
      // Single select display logic
      const singleValue = currentValues[0];
      const option = getOptionByValue(singleValue);
      return (
        <Div $flex $alignCenter $gap="12px" $minWidth="0">
          {option?.icon}
          {option?.label ?? (option?.name || String(singleValue))}
        </Div>
      );
    }
  };

  const availableOptions = options.filter(
    (opt) => !safeDisabledOptions.includes(opt.value)
  );

  return (
    <div>
      {label && (
        <P id={labelId} $mb="8" $ml="24" $mr="24" $center={$textCenter}>
          <b>{label}</b>
        </P>
      )}

      <AdminSelectWrapperStyled
        $opened={dropdown}
        onClick={() => setDropdown(!dropdown)}
        ref={dropdownRef}
        $shadowDesign={$shadowDesign}
        // a11y: combobox trigger — Tab-reachable, Enter/Space to open,
        // labeled by the visible <P> above (or by aria-label fallback),
        // points to the listbox via aria-controls.
        role="combobox"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={dropdown}
        aria-controls={listboxId}
        aria-labelledby={label ? labelId : undefined}
        aria-label={!label ? placeholder : undefined}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setDropdown((prev) => !prev);
          }
        }}
      >
        <AdminSelectWrapperChildStyled>
          <Div $flex $center={$textCenter} $width="100%" $minWidth="0">
            <SelectedStyled>
              <RenderSelectedItems />
            </SelectedStyled>
          </Div>

          <ChevronStyled>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.29365 12.7066C7.68428 13.0973 8.31865 13.0973 8.70928 12.7066L14.7093 6.70664C15.0999 6.31602 15.0999 5.68164 14.7093 5.29102C14.3187 4.90039 13.6843 4.90039 13.2937 5.29102L7.9999 10.5848L2.70615 5.29414C2.31553 4.90352 1.68115 4.90352 1.29053 5.29414C0.899902 5.68477 0.899902 6.31914 1.29053 6.70977L7.29053 12.7098L7.29365 12.7066Z"
                fill="#4C4C4C"
              />
            </svg>
          </ChevronStyled>
        </AdminSelectWrapperChildStyled>

        <DropdownMenuComponent isOpen={dropdown} maxHeight={'270px'} fullWidth>
          <Div
            id={listboxId}
            role="listbox"
            aria-multiselectable={multiSelect || undefined}
            $flex
            $column
            $gap="8px"
          >
            {availableOptions.map((option, i) => (
              <SelectOption
                role="option"
                tabIndex={dropdown ? 0 : -1}
                aria-selected={isSelected(option.value)}
                selected={isSelected(option.value)}
                // The options sit inside the wrapper, whose own click and
                // Enter/Space toggle the menu, so without stopping the event a
                // multi select would close after one pick. A single select
                // still closes: handleSelect does it itself. (The provider
                // select avoids this by only ever opening on the wrapper.)
                // A key is always stopped: the wrapper flips the menu from its
                // latest state, so after handleSelect closed it, Enter would
                // open it again. Focus then goes back to the closed select.
                onClick={(event) => {
                  if (multiSelect) event.stopPropagation();
                  handleSelect(option.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelect(option.value);
                    if (!multiSelect)
                      (dropdownRef.current as HTMLElement | null)?.focus();
                  }
                }}
                key={i}
              >
                <Div $flex $alignCenter $gap="12px" $minWidth="0">
                  {option.icon}
                  {option.label ?? option.name}
                </Div>
                {multiSelect && <Radio checked={isSelected(option.value)} />}
              </SelectOption>
            ))}
          </Div>
          {availableOptions.length < 1 && (
            <Disclaimer $m="0">{emptyPlaceholder}</Disclaimer>
          )}
        </DropdownMenuComponent>
      </AdminSelectWrapperStyled>
    </div>
  );
};
